import {
    CARRIER_TABLE_CELLS,
    FTL_CARRIER_TABLE_CELLS,
    MARKETPLACE_TABLE_CELLS,
    SUPPLIER_TABLE_CELLS,
    CUSTOMER_TABLE_CELLS,
    BANK_ACCOUNT_SUMMARY_TABLE_CELLS,
    CREDIT_CARD_BALANCES_SUMMARY_TABLE_CELLS,
    OUTSTANDING_CHEQUES_SUMMARY_TABLE_CELLS,
    SALES_TABLE_CELLS,
    STOCK_IN_HAND_TABLE_CELLS,
    LEDGER_ENTRIES_CELLS,
    LEDGER_PAYABLE_ENTRIES_CELLS,
    MARKETPLACE_MAPPING,
} from '../constants';

// Section blueprints. `oldKey`/`newKey` are the field names on the OLD (EditLedger state)
// and NEW (preview response) payloads. `cellConfig` drives the displayed columns;
// `idField` identifies a row; `compareFields` are the columns whose change flips a row to
// "changed" (defaults to every non-id column in cellConfig).
const SECTION_BLUEPRINTS = {
    creditCardBalances: { title: 'Credit Card Balances', oldKey: 'creditCardBalancesSummary', newKey: 'creditCardBalancesSummary', cellConfig: CREDIT_CARD_BALANCES_SUMMARY_TABLE_CELLS, idField: 'name' },
    carrier:            { title: 'Standard Carrier',     oldKey: 'carrierSummary',             newKey: 'carrierSummary',             cellConfig: CARRIER_TABLE_CELLS,                     idField: 'carrierName' },
    ftl:                { title: 'LTL/FTL Carrier',      oldKey: 'ftlSummary',                 newKey: 'ftlSummary',                 cellConfig: FTL_CARRIER_TABLE_CELLS,                 idField: 'carrierName' },
    supplier:           { title: 'Supplier',             oldKey: 'supplierSummary',            newKey: 'supplierSummary',            cellConfig: SUPPLIER_TABLE_CELLS,                    idField: 'supplierName' },
    stock:              { title: 'Stock in Hand',        oldKey: 'stockSummary',               newKey: 'stockSummary',               cellConfig: STOCK_IN_HAND_TABLE_CELLS,               idField: 'name' },
    sales:              { title: 'Sales',                oldKey: 'salesSummary',               newKey: 'salesSummary',               cellConfig: SALES_TABLE_CELLS,                       idField: 'name', mapName: true },
    marketplace:        { title: 'Marketplace',          oldKey: 'marketplaceSummary',         newKey: 'marketplaceSummary',         cellConfig: MARKETPLACE_TABLE_CELLS,                 idField: 'name', mapName: true },
    customer:           { title: 'Customer',             oldKey: 'customerSummary',            newKey: 'customerSummary',            cellConfig: CUSTOMER_TABLE_CELLS,                    idField: 'name' },
    bankAccount:        { title: 'Bank Balances',        oldKey: 'bankAccountSummary',         newKey: 'bankAccountSummary',         cellConfig: BANK_ACCOUNT_SUMMARY_TABLE_CELLS,        idField: 'name' },
    outstandingCheques: { title: 'Outstanding Cheques',  oldKey: 'outstandingChequesSummary',  newKey: 'outstandingChequesSummary',  cellConfig: OUTSTANDING_CHEQUES_SUMMARY_TABLE_CELLS, idField: 'name' },
    // Ledger entries: OLD comes from EditLedger `templates`, NEW from preview `ledgerEntries`.
    // Match on the stable transactionId (descriptions repeat across rows and the running balance
    // cascades, so a description match drops/mis-aligns refreshed entries). The displayed columns
    // mirror the live table and are chosen per ledger type in buildLedgerDiff (predefined vs custom).
    ledgerEntries:      { title: 'Ledger Entries',       oldKey: 'templates',                  newKey: 'ledgerEntries',              cellConfig: LEDGER_ENTRIES_CELLS,                    idField: 'description', matchField: 'transactionId' },
};

// Which sections each ledger shows, in display order. Exported so the live EditLedger view can
// drive which sub-tables it renders from the same source of truth as the audit preview.
export const LEDGER_SECTIONS = {
    'Accounts Payable':       ['creditCardBalances', 'carrier', 'ftl', 'supplier', 'ledgerEntries'],
    'Cost of Sales':          ['stock', 'carrier', 'ftl', 'supplier', 'ledgerEntries'],
    'Sales':                  ['sales', 'customer', 'ledgerEntries'],
    'Accounts Receivables':   ['marketplace', 'customer', 'ledgerEntries'],
    'Cash':                   ['bankAccount', 'outstandingCheques', 'ledgerEntries'],
};

const flattenEntries = (list) => {
    if (!Array.isArray(list)) return [];
    return list.reduce((acc, e) => {
        if (e && Array.isArray(e.entries)) return acc.concat(e.entries);
        acc.push(e);
        return acc;
    }, []);
};

const isNumericLike = (v) => {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'number') return true;
    return !isNaN(parseFloat(String(v).replace(/,/g, '')));
};

// Normalize a value for comparison (money rounded to 2 decimals, strings trimmed).
const normalizeForCompare = (v) => {
    if (v === null || v === undefined) return '';
    if (isNumericLike(v)) return parseFloat(String(v).replace(/,/g, '')).toFixed(2);
    return String(v).trim();
};

const rowId = (row, idField) => normalizeForCompare(row?.[idField]);

const isTotalRow = (row, idField) => String(row?.[idField] ?? '').trim().toLowerCase() === 'total';

const buildSection = (blueprint, oldData, newData) => {
    const idField = blueprint.idField;
    // The field used to match OLD vs NEW rows. Defaults to idField, but ledger entries key on the
    // stable transactionId so refreshed entries (repeated descriptions, cascading balances) align.
    const matchField = blueprint.matchField || idField;
    const compareFields = blueprint.compareFields
        || blueprint.cellConfig.map(c => c.id).filter(id => id !== idField);

    // Match key, with a description+date fallback when the matchField value is missing.
    const keyOf = (row) => {
        const primary = rowId(row, matchField);
        if (primary !== '') return primary;
        return `${rowId(row, 'description')}|${rowId(row, 'date')}`;
    };

    let oldRows = blueprint.oldKey === 'templates' ? flattenEntries(oldData[blueprint.oldKey]) : (oldData[blueprint.oldKey] || []);
    let newRows = blueprint.newKey === 'ledgerEntries' ? flattenEntries(newData[blueprint.newKey]) : (newData[blueprint.newKey] || []);

    oldRows = (oldRows || []).filter(r => r && !isTotalRow(r, idField));
    newRows = (newRows || []).filter(r => r && !isTotalRow(r, idField));

    const oldById = new Map();
    oldRows.forEach(r => oldById.set(keyOf(r), r));
    const newById = new Map();
    newRows.forEach(r => newById.set(keyOf(r), r));

    const buildFields = (oldRow, newRow) => {
        const fields = {};
        // captureFields are kept (but not displayed) so synthetic total rows can read them — e.g.
        // the Accounts Receivables / Cash grand total is stored on the entry's debit, not credit.
        const ids = [...new Set([...blueprint.cellConfig.map(c => c.id), ...(blueprint.captureFields || [])])];
        ids.forEach((id) => {
            const oldVal = oldRow ? oldRow[id] : undefined;
            const newVal = newRow ? newRow[id] : undefined;
            const comparable = compareFields.includes(id);
            const changed = comparable && !!oldRow && !!newRow
                && normalizeForCompare(oldVal) !== normalizeForCompare(newVal);
            fields[id] = { old: oldVal, new: newVal, changed };
        });
        return fields;
    };

    const rows = [];
    // Removed + changed + unchanged, in the OLD order, then appended additions.
    oldRows.forEach((oldRow) => {
        const id = keyOf(oldRow);
        const newRow = newById.get(id);
        if (!newRow) {
            rows.push({ id, status: 'removed', fields: buildFields(oldRow, null) });
        } else {
            const fields = buildFields(oldRow, newRow);
            const changed = Object.values(fields).some(f => f.changed);
            rows.push({ id, status: changed ? 'changed' : 'unchanged', fields });
        }
    });
    newRows.forEach((newRow) => {
        const id = keyOf(newRow);
        if (!oldById.has(id)) {
            rows.push({ id, status: 'added', fields: buildFields(null, newRow) });
        }
    });

    // Synthetic Total / grand-total rows so the predefined audit mirrors the live ledger tables
    // (each section ends with a bold "Total", and the manual "Other" section also shows the bold
    // "<ledgerName> Total" grand total). Driven by blueprint flags, set only for predefined ledgers.
    const labelCol = blueprint.cellConfig[0].id;
    const amountField = compareFields[0];
    const toNum = (v) => {
        if (v === null || v === undefined || v === '') return 0;
        const s = String(v).replace(/[(),]/g, '').trim();
        const n = parseFloat(s);
        return isNaN(n) ? 0 : n;
    };
    const totalRow = (key, label, amtOld, amtNew) => {
        const changed = toNum(amtOld) !== toNum(amtNew);
        const fields = {};
        blueprint.cellConfig.forEach(({ id }) => {
            if (id === labelCol) fields[id] = { old: label, new: label, changed: false };
            else if (id === amountField) fields[id] = { old: amtOld, new: amtNew, changed };
            else fields[id] = { old: '', new: '', changed: false };
        });
        return { id: key, status: changed ? 'changed' : 'unchanged', bold: true, isTotal: true, fields };
    };

    if (blueprint.grandTotal) {
        // Pull the grand-total entry (description === ledger name) out of the data rows so it shows
        // as its own bold "<ledgerName> Total" line instead of a blank data row.
        const gIdx = rows.findIndex(r => {
            const v = r.fields[labelCol] ? (r.fields[labelCol].new !== undefined ? r.fields[labelCol].new : r.fields[labelCol].old) : undefined;
            return String(v ?? '').trim() === blueprint.grandTotal.description;
        });
        const grandRow = gIdx >= 0 ? rows.splice(gIdx, 1)[0] : null;

        // Only roll up a section "Total" when there are data rows to total — an empty section (e.g.
        // "Other" with no manual entries) shows just its grand total, no redundant zero Total.
        if (blueprint.totalRow && rows.length > 0) {
            const sumOld = rows.reduce((a, r) => a + toNum(r.fields[amountField] && r.fields[amountField].old), 0);
            const sumNew = rows.reduce((a, r) => a + toNum(r.fields[amountField] && r.fields[amountField].new), 0);
            rows.push(totalRow('__total__', 'Total', sumOld.toFixed(2), sumNew.toFixed(2)));
        }
        if (grandRow) {
            const gf = blueprint.grandTotal.fromDebit ? 'debit' : amountField;
            const gOld = grandRow.fields[gf] ? grandRow.fields[gf].old : undefined;
            const gNew = grandRow.fields[gf] ? grandRow.fields[gf].new : undefined;
            rows.push(totalRow('__grandtotal__', `${blueprint.grandTotal.description} Total`, gOld, gNew));
        }
    } else if (blueprint.totalRow && rows.length > 0) {
        const sumOld = rows.reduce((a, r) => a + toNum(r.fields[amountField] && r.fields[amountField].old), 0);
        const sumNew = rows.reduce((a, r) => a + toNum(r.fields[amountField] && r.fields[amountField].new), 0);
        rows.push(totalRow('__total__', 'Total', sumOld.toFixed(2), sumNew.toFixed(2)));
    }

    // Total rows are layout, not findings — keep them out of the change counts.
    const counts = rows.reduce((acc, r) => {
        if (!r.isTotal && r.status !== 'unchanged') acc[r.status] = (acc[r.status] || 0) + 1;
        return acc;
    }, { changed: 0, added: 0, removed: 0 });

    return {
        key: blueprint.key,
        title: blueprint.title,
        cellConfig: blueprint.cellConfig,
        idField,
        mapName: !!blueprint.mapName,
        rows,
        counts,
    };
};

/**
 * Builds an audit diff between the currently-displayed ledger data (oldData) and the
 * recomputed-but-unsaved refresh result (newData).
 *
 * @param {string} ledgerName   The ledger's name (selects which sections apply).
 * @param {object} oldData      EditLedger state: summary arrays + `templates`.
 * @param {object} newData      Preview response: summary arrays + `ledgerEntries`.
 * @returns {{ sections: Array, counts: {changed:number, added:number, removed:number} }}
 */
export const buildLedgerDiff = (ledgerName, oldData = {}, newData = {}) => {
    // Predefined ledgers are exactly the named keys of LEDGER_SECTIONS; anything else is custom.
    const isPredefined = !!LEDGER_SECTIONS[ledgerName];
    const sectionKeys = LEDGER_SECTIONS[ledgerName] || ['ledgerEntries'];

    const sections = sectionKeys.map((key) => {
        const blueprint = { ...SECTION_BLUEPRINTS[key], key };
        if (key === 'ledgerEntries') {
            // Mirror exactly what the live entries table shows for this ledger type. Predefined
            // ledgers render the manual block as an "Other" table (Other + Amount only) capped with a
            // "Total" and a "<ledgerName> Total" grand total; custom ledgers show the full
            // Date / Description / Debit / Credit / Balance entries table with no synthetic totals.
            if (isPredefined) {
                blueprint.title = 'Other';
                blueprint.cellConfig = LEDGER_PAYABLE_ENTRIES_CELLS;
                blueprint.compareFields = ['credit'];
                blueprint.totalRow = true;
                blueprint.captureFields = ['debit'];
                blueprint.grandTotal = {
                    description: ledgerName,
                    // AR / Cash store their grand total on debit; the other predefined ledgers on credit.
                    fromDebit: ledgerName === 'Accounts Receivables' || ledgerName === 'Cash',
                };
            } else {
                blueprint.title = `Total ${ledgerName}`;
                blueprint.cellConfig = LEDGER_ENTRIES_CELLS;
                blueprint.compareFields = ['debit', 'credit', 'balance'];
            }
        } else if (isPredefined) {
            // Each predefined summary sub-table (Marketplace, Customer, ...) ends with a bold Total.
            blueprint.totalRow = true;
        }
        return buildSection(blueprint, oldData, newData);
    });

    const counts = sections.reduce((acc, s) => {
        acc.changed += s.counts.changed;
        acc.added += s.counts.added;
        acc.removed += s.counts.removed;
        return acc;
    }, { changed: 0, added: 0, removed: 0 });

    return { sections, counts };
};

// Human-friendly display for a cell value (maps marketplace keys, formats money).
export const displayValue = (value, cellId, mapName) => {
    if (value === null || value === undefined || value === '') return '';
    if ((cellId === 'name') && mapName) {
        return MARKETPLACE_MAPPING[value] || value;
    }
    if (cellId === 'date') {
        return String(value).split('T')[0];
    }
    if (isNumericLike(value) && cellId !== 'name' && cellId !== 'carrierName' &&
        cellId !== 'supplierName' && cellId !== 'description') {
        return parseFloat(String(value).replace(/,/g, ''))
            .toFixed(2)
            .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
    }
    return String(value);
};
